// Dashboard read-query grammar (arc 101 slice 09, task #79) — the READ plane a
// dashboard widget queries, plus the pager negotiation contract. Adopted from
// tesote.ai's proven grammar (`packages/shared/src/data-sources.ts`), narrowed to
// this platform's three sources.
//
// READ-ONLY BY CONSTRUCTION: there is no insert/update/delete/send verb anywhere,
// `http` admits only `GET`/`HEAD`, and the `platform` plane exposes a fixed
// resource allow-list. A write-shaped query is therefore UNREPRESENTABLE — it
// fails to parse, never reaching the resolver, which re-validates server-side
// (defence in depth) before any data is read.
//
// Pure: imports only Zod + the shared sort/filter vocabulary from `list-query`.
// No I/O, nothing internal beyond the domain boundary. Queries are authored (agent
// `dashboard_upsert` / seed), stored as jsonb, and parsed on read.

import { z } from 'zod';
import { IntegrationIdSchema } from './ids';
import { FILTER_OPS } from './list-query';

// ─── shared sort / filter vocabulary ─────────────────────────────────────────
// One vocabulary across the whole platform: the ops + directions are the same
// `list-query` speaks for `GET` collections, so a dashboard filter and a REST
// filter never diverge.

/** Sort direction — `asc` | `desc`. The Zod schema for list-query's `SortDir`
 * union (that type is re-exported from `list-query`; this adds the schema). */
export const SortDirSchema = z.enum(['asc', 'desc']);

/** One sort term over a result-set `key`. */
export const WidgetSortSchema = z
  .object({
    key: z.string().min(1).max(128),
    dir: SortDirSchema,
  })
  .strict();
export type WidgetSort = z.infer<typeof WidgetSortSchema>;

/** One filter term — `key <op> values`. `eq`/`ne` carry one value, `in` many.
 * Ops are the read-only set from list-query — no write/exec operator exists. */
export const WidgetFilterSchema = z
  .object({
    key: z.string().min(1).max(128),
    op: z.enum(FILTER_OPS),
    values: z.array(z.string()).min(1),
  })
  .strict();
export type WidgetFilter = z.infer<typeof WidgetFilterSchema>;

// ─── capabilities — the negotiable control surface ───────────────────────────
// What a widget lets the CLIENT change without an agent turn. The client renders
// sort/filter controls from these keys; the pager (`pageRequestSchema`) rejects
// anything outside them. `paginate:false` = a single fixed page (no cursor/offset).

export const WidgetCapabilitiesSchema = z
  .object({
    /** Result keys the client may sort by. Empty = sorting disabled. */
    sort: z.array(z.string().min(1).max(128)).default([]),
    /** Result keys the client may filter by. Empty = filtering disabled. */
    filter: z.array(z.string().min(1).max(128)).default([]),
    /** Whether the pager serves further pages. False = the first page is all. */
    paginate: z.boolean().default(false),
  })
  .strict();
export type WidgetCapabilities = z.infer<typeof WidgetCapabilitiesSchema>;

/** The zero-capability surface — a static, single-page widget. */
export const NO_WIDGET_CAPABILITIES: WidgetCapabilities = { sort: [], filter: [], paginate: false };

// ─── platform read plane — the resource allow-list ───────────────────────────

/** The platform collections a widget may read. Each maps to a server-side
 * resolver over an existing read model; extend HERE to expose a new collection
 * (the resolver must then serve it). Naming mirrors the REST / list-query surface. */
export const PlatformResource = {
  Repos: 'repos',
  Issues: 'issues',
  PullRequests: 'prs',
  Tasks: 'tasks',
  Runners: 'runners',
  Sessions: 'sessions',
  AuditEvents: 'audit_events',
  CiJobs: 'ci_jobs',
  RunEvents: 'run_events',
  Escalations: 'escalations',
} as const;
export const PlatformResourceSchema = z.enum(PlatformResource);
export type PlatformResource = z.infer<typeof PlatformResourceSchema>;
/** Every platform resource, in catalog order — for iteration / allow-list checks. */
export const PLATFORM_RESOURCES: readonly PlatformResource[] = PlatformResourceSchema.options;

// ─── the read-query union — platform | http | mcp ────────────────────────────
// Discriminated on `source`. NO branch can express a write: `platform` names a
// read resource, `http` admits only GET|HEAD, `mcp` invokes a tool the server
// re-checks against the account's read allow-list.

export const WidgetSource = {
  Platform: 'platform',
  Http: 'http',
  Mcp: 'mcp',
} as const;
export const WidgetSourceSchema = z.enum(WidgetSource);
export type WidgetSource = z.infer<typeof WidgetSourceSchema>;

/** Read a platform collection (the internal-first source). `filters`/`sort` scope
 * the read at author time; the pager LAYERS the client's negotiated sort/filter on
 * top. No mutation field exists. */
export const PlatformQuerySchema = z
  .object({
    source: z.literal(WidgetSource.Platform),
    resource: PlatformResourceSchema,
    filters: z.array(WidgetFilterSchema).optional(),
    sort: z.array(WidgetSortSchema).optional(),
  })
  .strict();

/** Read an integration HTTP endpoint. Only safe verbs — credentials are injected
 * server-side from the connected integration, never client-held. */
export const HttpQuerySchema = z
  .object({
    source: z.literal(WidgetSource.Http),
    /** The connected integration whose base URL + sealed credential back this read. */
    integrationId: IntegrationIdSchema,
    /** Path relative to the integration's base URL (e.g. `/v2/issues`). */
    path: z.string().min(1).max(2048),
    /** Read verbs only — the schema itself cannot express a write. */
    method: z.enum(['GET', 'HEAD']).default('GET'),
    /** Query params merged on top of the integration's defaults. */
    params: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).optional(),
    /** Dotted JSON path to the row array in the response body. */
    rows_path: z.string().max(256).optional(),
  })
  .strict();

/** Invoke an MCP tool that returns tabular data. The tool must be in the account's
 * read allow-list — the resolver rejects anything else before a grant is minted.
 * `args` are forwarded verbatim; a write tool is fenced by the allow-list, not the
 * shape (mirrors tesote's `tool_allow`). */
export const McpQuerySchema = z
  .object({
    source: z.literal(WidgetSource.Mcp),
    tool: z.string().min(1).max(128),
    args: z.record(z.string(), z.unknown()).optional(),
    rows_path: z.string().max(256).optional(),
  })
  .strict();

/** What every data widget carries — the source-agnostic, read-only request. */
export const WidgetQuerySchema = z.discriminatedUnion('source', [
  PlatformQuerySchema,
  HttpQuerySchema,
  McpQuerySchema,
]);
export type WidgetQuery = z.infer<typeof WidgetQuerySchema>;

// ─── pager contract — one endpoint, capability-gated ─────────────────────────
// `POST /v1/dashboards/:id/widgets/:wid/page` body → `{rows, cursor?, total?}`.
// Sort/filter/pagination NEVER cost an agent turn: the client drives them within
// the widget's declared capabilities, and `pageRequestSchema` is the fence.

/** Where in the result set to read — an opaque `cursor` OR a numeric `offset`. */
export const PagePositionSchema = z.union([
  z.object({ cursor: z.string().min(1).max(512) }).strict(),
  z.object({ offset: z.number().int().nonnegative() }).strict(),
]);
export type PagePosition = z.infer<typeof PagePositionSchema>;

/** The raw pager body, shape-only (no capability check). Use {@link pageRequestSchema}
 * to additionally enforce a widget's capabilities. */
export const PageRequestSchema = z
  .object({
    page: PagePositionSchema.optional(),
    sort: z.array(WidgetSortSchema).optional(),
    filters: z.array(WidgetFilterSchema).optional(),
  })
  .strict();
export type PageRequest = z.infer<typeof PageRequestSchema>;

/**
 * Build the pager-body validator for one widget's {@link WidgetCapabilities}. The
 * returned schema parses a {@link PageRequest} and REJECTS (a 400, never a silent
 * drop) any sort/filter key outside the capability allow-list, or pagination when
 * the widget declares `paginate:false`. This is the server-side enforcement the
 * pager route runs; the client renders controls from the SAME capabilities, so a
 * well-behaved client never trips it.
 */
export function pageRequestSchema(capabilities: WidgetCapabilities) {
  const sortable = new Set(capabilities.sort);
  const filterable = new Set(capabilities.filter);
  return PageRequestSchema.superRefine((v, ctx) => {
    if (v.page !== undefined && !capabilities.paginate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'this widget is not paginated',
        path: ['page'],
      });
    }
    for (const term of v.sort ?? []) {
      if (!sortable.has(term.key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `cannot sort by "${term.key}" (sortable: ${listOrNone(sortable)})`,
          path: ['sort'],
        });
      }
    }
    for (const term of v.filters ?? []) {
      if (!filterable.has(term.key)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `cannot filter by "${term.key}" (filterable: ${listOrNone(filterable)})`,
          path: ['filters'],
        });
      }
    }
  });
}

function listOrNone(set: ReadonlySet<string>): string {
  return set.size > 0 ? [...set].join(', ') : 'none';
}

// ─── pager response ──────────────────────────────────────────────────────────

/** One result row — opaque JSON cells keyed by column. The renderer + column
 * descriptors decide how each cell is shown. */
export const PageRowSchema = z.record(z.string(), z.unknown());
export type PageRow = z.infer<typeof PageRowSchema>;

/** The pager response: this page's `rows`, the `cursor` for the next page (null =
 * last), and `total` when the source can count cheaply. */
export const PageResponseSchema = z
  .object({
    rows: z.array(PageRowSchema),
    cursor: z.string().nullable().optional(),
    total: z.number().int().nonnegative().optional(),
  })
  .strict();
export type PageResponse = z.infer<typeof PageResponseSchema>;
